import { faWindowMaximize } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, forwardRef, Children, useEffect } from 'react';
import type { ReactNode, ElementRef, ComponentPropsWithoutRef } from 'react';

import cn from 'classnames';

import {
  NestedAccordionsStoreProvider,
  useNestedAccordionsStore,
  NestedAccordionsValues,
} from './nested-accordions-store';
import { Accordion } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/experimental-code-block/ui/accordion';
import { Button } from '@scale/scaleui';

type NestedAccordionsProps = {
  className?: string;
  children: ReactNode | ReactNode[];
};

export function NestedAccordionsRoot({ className, children }: NestedAccordionsProps) {
  return (
    <NestedAccordionsStoreProvider>
      <NestedAccordionsWrapper className={className}>{children}</NestedAccordionsWrapper>
    </NestedAccordionsStoreProvider>
  );
}

type NestedAccordionsWrapperProps = {
  className?: string;
  children: ReactNode | ReactNode[];
};

function NestedAccordionsWrapper({ className, children }: NestedAccordionsWrapperProps) {
  const { globalValue, onChangeGlobalValue } = useNestedAccordionsStore(state => ({
    globalValue: state.globalValue,
    onChangeGlobalValue: state.onChangeGlobalValue,
  }));

  useEffect(() => {
    // Default expand JSON
    onChangeGlobalValue(NestedAccordionsValues.ExpandAll);
  }, [onChangeGlobalValue]);

  return (
    <div className={cn('relative group', className)}>
      <Button
        className="absolute top-2.5 right-3 h-6 group-hover:flex gap-2 bg-transparent hover:bg-secondary hidden"
        size={'sm'}
        onClick={event => {
          event.stopPropagation();
          onChangeGlobalValue(
            globalValue === NestedAccordionsValues.CollapseAll
              ? NestedAccordionsValues.ExpandAll
              : NestedAccordionsValues.CollapseAll,
          );
        }}
      >
        <FontAwesomeIcon icon={faWindowMaximize} />
        {globalValue === NestedAccordionsValues.CollapseAll ? 'Expand all' : 'Collapse all'}
      </Button>
      {children}
    </div>
  );
}

export const NestedAccordion = forwardRef<
  ElementRef<typeof Accordion>,
  Omit<ComponentPropsWithoutRef<typeof Accordion>, 'type' | 'values' | 'onChange'>
>(({ children, defaultValue, ...props }, ref) => {
  const [values, setValues] = useState((defaultValue as string[]) ?? []);
  const { globalValue, onChangeGlobalValue } = useNestedAccordionsStore(state => ({
    globalValue: state.globalValue,
    onChangeGlobalValue: state.onChangeGlobalValue,
  }));

  const childrenCount = Children.count(children);

  useEffect(() => {
    setValues(values => {
      switch (globalValue) {
        case NestedAccordionsValues.ExpandAll:
          return [...Array(childrenCount).keys()].map((_, index) => `item-${index}`);
        case NestedAccordionsValues.CollapseAll:
          return [];
        default:
          return values;
      }
    });
  }, [globalValue, childrenCount]);

  return (
    <Accordion
      ref={ref}
      {...props}
      type="multiple"
      value={values}
      onValueChange={values => {
        if (globalValue !== NestedAccordionsValues.None) {
          onChangeGlobalValue(NestedAccordionsValues.None);
        }
        setValues(values);
      }}
    >
      {children}
    </Accordion>
  );
});

NestedAccordion.displayName = 'NestedAccordion';
